/**
 * Format numbers with K, M, B suffixes
 * @param num - The number to format
 * @returns Formatted string with suffix
 */
export function formatNumWithKMB(num: number): string {
    if (num === null || num === undefined) return '';
    
    const absNum = Math.abs(num);
    
    if (absNum >= 1e9) {
        const formatted = (num / 1e9).toFixed(2);
        // Remove trailing zeros and decimal point if not needed
        return formatted.replace(/\.?0+$/, '') + 'B';
    }
    if (absNum >= 1e6) {
        const formatted = (num / 1e6).toFixed(2);
        // Remove trailing zeros and decimal point if not needed
        return formatted.replace(/\.?0+$/, '') + 'M';
    }
    if (absNum >= 1e3) {
        const formatted = (num / 1e3).toFixed(2);
        // Remove trailing zeros and decimal point if not needed
        return formatted.replace(/\.?0+$/, '') + 'K';
    }
    
    // For numbers less than 1000, show as integer if whole number, otherwise 2 decimal places
    if (Number.isInteger(num)) {
        return num.toString();
    }
    return num.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Like formatNumWithKMB but never strips fractional zeros: always two digits after the decimal
 * when a fractional part is shown (e.g. 7.9 → "7.90"). Whole numbers under 1000 stay unpadded (e.g. 12).
 * K/M/B scaled values always use two decimal places (e.g. "5.00K").
 */
export function formatNumWithKMBTwoDecimalPlaces(num: number): string {
    if (num === null || num === undefined) return '';

    const absNum = Math.abs(num);

    if (absNum >= 1e9) {
        return `${(num / 1e9).toFixed(2)}B`;
    }
    if (absNum >= 1e6) {
        return `${(num / 1e6).toFixed(2)}M`;
    }
    if (absNum >= 1e3) {
        return `${(num / 1e3).toFixed(2)}K`;
    }

    if (Number.isInteger(num)) {
        return num.toString();
    }
    return num.toFixed(2);
}

/**
 * Truncate text to a specified length
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis
 */
export function truncate(text: string, maxLength: number = 15): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
}

/** Font family for all chart text (labels, axis points, data values) - hard rule */
export const CHART_FONT_FAMILY = 'Open Sans';

/** Font family with fallback for canvas/CSS (quoted for names with spaces) */
export const CHART_FONT_FAMILY_CSS = "'Open Sans', sans-serif";

/** Max character length for x-axis labels before ellipsis (used across bar, line, BPL charts) */
export const X_AXIS_LABEL_MAX_LENGTH = 30;

/** Max characters per line when wrapping axis labels */
export const AXIS_LABEL_WRAP_CHARS = 24;

/** Max lines for wrapped axis labels before ellipsis */
export const AXIS_LABEL_MAX_LINES = 2;

/** Max lines for fixed-spacing (V2) x-axis tick labels before ellipsis. */
export const AXIS_LABEL_FIXED_SPACING_MAX_LINES = 3;

/**
 * Average rendered glyph width (px) for 13px Open Sans axis labels. Used to convert the fixed
 * pixel space allocated to each x-axis tick into a per-line character budget. Deliberately
 * conservative (real mixed-case text averages a little under this) so estimated line widths
 * never under-shoot the rendered width and cause collisions.
 */
export const AXIS_LABEL_AVERAGE_CHAR_WIDTH_PX = 7.5;

/**
 * Horizontal breathing room (px) kept clear between two adjacent, centered tick labels. Subtracted
 * from each tick's allocated fixed space before computing the character budget so labels never touch.
 */
export const AXIS_LABEL_TICK_SAFETY_GAP_PX = 16;

/**
 * Format x-axis label: wrap if needed, truncate with ellipsis if longer than maxLength
 * Handles both string and non-string values (e.g. numeric x-axis like quarter numbers).
 * @param text - The label text (string or number/other - will be coerced to string)
 * @param maxLength - Max chars before ellipsis (default 24)
 * @returns Formatted label
 */
export function formatXAxisLabel(text: string | number | unknown, maxLength: number = X_AXIS_LABEL_MAX_LENGTH): string {
    if (text === null || text === undefined) return '';
    const str = String(text);
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
}

/**
 * Format axis label with wrapping: wrap into multiple lines at maxCharsPerLine (default 24),
 * then add ellipsis if still exceeds maxLines. Uses newline for wrapping (G2/SVG supports \n).
 * @param text - The label text
 * @param maxCharsPerLine - Max characters per line (default 24)
 * @param maxLines - Max lines before ellipsis (default 2)
 * @returns Formatted label with newlines for wrapping
 */
export function formatAxisLabelWithWrap(
    text: string,
    maxCharsPerLine: number = AXIS_LABEL_WRAP_CHARS,
    maxLines: number = AXIS_LABEL_MAX_LINES,
): string {
    if (!text) return '';
    if (text.length <= maxCharsPerLine) return text;

    const lines: string[] = [];
    let remaining = text;

    for (let i = 0; i < maxLines && remaining.length > 0; i++) {
        if (remaining.length <= maxCharsPerLine) {
            lines.push(remaining);
            remaining = '';
        } else {
            // Find a good break point (space) if possible, otherwise hard break
            const chunk = remaining.substring(0, maxCharsPerLine);
            const lastSpace = chunk.lastIndexOf(' ');
            const breakAt = lastSpace > maxCharsPerLine * 0.5 ? lastSpace : maxCharsPerLine;
            lines.push(remaining.substring(0, breakAt));
            remaining = remaining.substring(breakAt).trimStart();
        }
    }

    let result = lines.join('\n');
    if (remaining.length > 0) {
        result = result + '...';
    }
    return result;
}

function appendWithEllipsis(text: string, maxChars: number): string {
    const ellipsis = '...';
    if (maxChars <= ellipsis.length) return ellipsis.slice(0, Math.max(0, maxChars));
    if (text.length <= maxChars) return text;
    return `${text.slice(0, maxChars - ellipsis.length).trimEnd()}${ellipsis}`;
}

function splitLongToken(token: string, maxChars: number): { line: string; remaining: string } {
    if (token.length <= maxChars) return { line: token, remaining: '' };
    return {
        line: token.slice(0, maxChars),
        remaining: token.slice(maxChars),
    };
}

/**
 * Format a fixed-spacing (V2) x-axis tick label so it always stays within the fixed horizontal
 * space allocated to each tick — guaranteeing no collision between neighbouring labels without
 * relying on G2's `labelAutoHide`.
 *
 * `tickSpacePx` is the per-category band stride (e.g. `fixedBarGeometry.categoryStridePx`). A small
 * safety gap is reserved from it, and the remainder is converted to a per-line character budget.
 * Whole words move to the next line when they don't fit; a single word wider than one line is
 * hard-broken; anything still overflowing after `maxLines` is ellipsised on the final line.
 */
export function formatFixedSpacingXAxisLabel(
    text: string | number | unknown,
    tickSpacePx: number,
    maxLines: number = AXIS_LABEL_FIXED_SPACING_MAX_LINES,
    averageCharWidthPx: number = AXIS_LABEL_AVERAGE_CHAR_WIDTH_PX,
    safetyGapPx: number = AXIS_LABEL_TICK_SAFETY_GAP_PX,
): string {
    if (text === null || text === undefined) return '';

    const normalized = String(text).replace(/\s+/g, ' ').trim();
    if (!normalized) return '';

    const usableWidthPx = Math.max(averageCharWidthPx, tickSpacePx - safetyGapPx);
    const maxCharsPerLine = Math.max(1, Math.floor(usableWidthPx / averageCharWidthPx));
    if (normalized.length <= maxCharsPerLine) return normalized;

    const lines: string[] = [];
    let currentLine = '';
    let remaining = normalized;

    while (remaining && lines.length < maxLines) {
        const nextSpace = remaining.indexOf(' ');
        const nextWord = nextSpace === -1 ? remaining : remaining.slice(0, nextSpace);
        const candidate = currentLine ? `${currentLine} ${nextWord}` : nextWord;

        if (candidate.length <= maxCharsPerLine) {
            currentLine = candidate;
            remaining = nextSpace === -1 ? '' : remaining.slice(nextSpace + 1).trimStart();
            continue;
        }

        if (currentLine) {
            lines.push(currentLine);
            currentLine = '';
            continue;
        }

        const split = splitLongToken(nextWord, maxCharsPerLine);
        lines.push(split.line);
        remaining = `${split.remaining}${nextSpace === -1 ? '' : remaining.slice(nextSpace)}`.trimStart();
    }

    if (currentLine && lines.length < maxLines) {
        lines.push(currentLine);
        currentLine = '';
    }

    const leftover = `${currentLine} ${remaining}`.trim();
    if (leftover && lines.length > 0) {
        const lastIndex = lines.length - 1;
        lines[lastIndex] = appendWithEllipsis(`${lines[lastIndex]} ${leftover}`.trim(), maxCharsPerLine);
    }

    return lines.join('\n');
}





